// One standard contract for the chat agent to apply a bulk DOCUMENT mutation
// (font on all scenes, voice on all scenes, animate all images in a scene).
// Sibling of aiPopupGateway.js — plain data + functions, no classes.
// See docs/superpowers/specs/2026-06-11-agent-bulk-mutate-design.md (§3).
//
// The backend mutate-tools return a `mutationIntent` envelope; the card
// (BulkMutateToolUI) parses it and — for instant tiers — calls the matching
// executor here. Executors are pure-ish: they take (dispatch, getState, params,
// scope) from the card (which has Redux access via react-redux), so this module
// imports no store and stays trivially testable.

import { batchSetSceneFont, batchUpdateNarrationVoice } from "../../../common/actions.es6";
import { runRedesignScene } from "./runRedesignScene";

export const AI_MUTATION_EVENT = "aidea:applyMutation";
const PENDING = "__aideaPendingMutation";

// Auto-apply-once guard. Mirrors aiPopupGateway's shouldAutoOpenOnce: a restored
// conversation remounts every card, and instant cards auto-apply on mount — we
// must NOT re-apply a past font/voice change. Keyed by tool-call id, persisted in
// localStorage (conversations persist there too), capped to the most-recent N.
const AUTO_KEY = "aidea:autoAppliedMutations";
const AUTO_MAX = 300;

function autoSet() {
  try {
    return new Set(JSON.parse(localStorage.getItem(AUTO_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

export function shouldAutoApplyOnce(key) {
  if (typeof window === "undefined" || !key) return false;
  const set = autoSet();
  if (set.has(key)) return false;
  set.add(key);
  try {
    localStorage.setItem(AUTO_KEY, JSON.stringify([...set].slice(-AUTO_MAX)));
  } catch {
    /* localStorage full / unavailable — best-effort */
  }
  return true;
}

// Stash keyed by op so a late-mounting listener still drains it, then fire the
// event ONCE (no modal-mount race like the popup gateway has — a single fire is
// enough; the card itself owns apply). Ignores unknown ops (forward-compatible
// guard, same discipline as the popup gateway's isKnownFeature).
export function dispatchApplyMutation(payload) {
  if (typeof window === "undefined" || !payload || !payload.op || !MUTATION_REGISTRY[payload.op]) {
    return;
  }
  window[PENDING] = window[PENDING] || {};
  window[PENDING][payload.op] = payload;
  window.dispatchEvent(new CustomEvent(AI_MUTATION_EVENT, { detail: payload }));
}

// Read + clear the stashed payload for an op (one-shot).
export function drainPendingMutation(op) {
  if (typeof window === "undefined" || !window[PENDING]) return null;
  const p = window[PENDING][op] || null;
  if (p) window[PENDING][op] = null;
  return p;
}

// op → { tier, executor }. Executors take (dispatch, getState, params, scope).
// Instant ones mutate the store synchronously; the gated animate op has a null
// executor — the card calls runAnimateAllImages (Task 8) on confirm instead.
export const MUTATION_REGISTRY = {
  setSceneFontAll: {
    tier: "instant",
    executor: (dispatch, _getState, params) => dispatch(batchSetSceneFont(params.fontFamily)),
  },
  setNarrationVoiceAll: {
    tier: "instant",
    executor: (dispatch, _getState, params) => {
      const v = (params && params.voice) || {};
      dispatch(
        batchUpdateNarrationVoice({
          provider: v.provider,
          voiceName: v.name,
          voiceId: v.voiceId,
          language: v.language,
          gender: v.gender,
        })
      );
      // VERIFY-LIVE: also set the global default voice (common/ai_config.es6 window
      // vars + globalConfig) so future TTS generations use the new voice, not just
      // the existing soundList rows. The batch reducer covers existing sounds; the
      // global default write is the second half and needs the real ai_config keys.
    },
  },
  animateAllImagesInScene: {
    tier: "gated",
    // Async paid fan-out — implemented in runAnimateAllImages.js (Task 8). The
    // gated card calls it on the user's Apply click; never auto-applied.
    executor: null,
  },
  motionDirectorScene: {
    tier: "gated",
    // Paid design job — runMotionDirector.js on the card's Design click, then
    // applyMotionPlan.js on its "Apply to scene" click. Never auto-applied.
    executor: null,
  },
  redesignScene: {
    tier: "gated",
    // Paid server-side rebuild — runRedesignScene.js on the card's Redesign click. Never auto-applied.
    executor: runRedesignScene,
  },
  editMotionScene: {
    tier: "gated",
    // One edit to an existing motion scene — runEditMotionScene.js on the
    // card's Apply click, then applyMotionEdit() when the job returns the new
    // document. Never auto-applied: re-running it on a restored conversation
    // would edit the scene a second time, on top of the first edit.
    executor: null,
  },
};
